"use client";

import React, { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import type { Option } from "@/app/constants/creatorNetwork";

type SelectPillProps = {
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder: string;
  className?: string;
};

export default function SelectPill({
  name,
  value,
  onChange,
  options,
  placeholder,
  className = "",
}: SelectPillProps) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((opt) => opt.value === value) || null;

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setIsOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !selected) return;
    const activeEl = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    activeEl?.scrollIntoView({ block: "nearest" });
  }, [isOpen, selected]);

  function selectOption(optValue: string) {
    onChange(optValue);
    setIsOpen(false);
  }

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {/* Hidden native select so this still behaves like a real form field
          (name/value participate in form submission, and it's a fallback
          for anything that reads the DOM as a form). Visually hidden only —
          the custom button + list below is what users see and interact with,
          since native <option> font-size can't be styled consistently across
          mobile browsers. */}
      <select
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`w-full flex items-center justify-between gap-3 bg-transparent border border-white/40 rounded-full px-6 py-[0.7rem] text-left text-font-18 outline-none cursor-pointer ${
          selected ? "text-white" : "text-white/60"
        }`}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDownIcon
          className={`h-5 w-5 shrink-0 text-white transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <ul
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          data-lenis-prevent
          className="absolute z-30 mt-2 w-full max-h-64 overflow-y-auto rounded-3xl border border-white/10 bg-grey py-2 shadow-[0_20px_45px_-15px_rgba(0,0,0,0.6)]"
        >
          {options.map((opt) => {
            const isActive = opt.value === value;
            return (
              <li key={opt.value} role="option" aria-selected={isActive} data-active={isActive}>
                <button
                  type="button"
                  onClick={() => selectOption(opt.value)}
                  className={`w-full text-left px-6 py-2.5 text-font-18 transition-colors ${
                    isActive ? "text-purple font-semibold bg-grey/5" : "text-black/80 hover:bg-black/5"
                  }`}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
